import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { api, ensureOk, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { Encabezado } from '@/components/Estados';
import { nombreEspecialidad, useEspecialidades } from '@/features/preguntas/queries';
import { leerBase64, validarImagen } from '@/lib/archivos';
import { fechaHora } from '@/lib/formato';

import { useTrabajos } from './queries';

type Body = components['schemas']['AiGenerateBody'];
type Resultado = components['schemas']['AiGenerateResult'];

const ESTADO_TRABAJO: Record<string, string> = {
  queued: 'En cola',
  running: 'Generando',
  done: 'Terminado',
  failed: 'Con fallas',
  canceled: 'Cancelado',
};

function Generar() {
  const qc = useQueryClient();
  const editable = useCan('ia', 'editar');
  const esp = useEspecialidades();
  const [f, setF] = useState<Body>({
    task: 'caso_desde_texto',
    difficulty: 'intermedio',
    language: 'es',
    questions: 1,
    count: 1,
    topic: '',
  });
  const [imagen, setImagen] = useState<File | null>(null);
  const [ultimo, setUltimo] = useState<Resultado | null>(null);
  const trabajos = useTrabajos(true);
  const hayActivos = (trabajos.data ?? []).some(
    (t) => t.status === 'queued' || t.status === 'running',
  );
  const poner = (c: Partial<Body>) => setF((v) => ({ ...v, ...c }));
  const desdeImagen = f.task === 'caso_desde_imagen';

  const generar = useMutation({
    mutationFn: async () => {
      const body: Body = { ...f };
      if (desdeImagen) {
        if (!imagen) throw new Error('Adjunta una imagen.');
        const err = validarImagen(imagen);
        if (err) throw new Error(err);
        body.image = await leerBase64(imagen);
        body.count = Math.min(body.count ?? 1, 3);
      }
      if (!body.sourceText) delete body.sourceText;
      if (!body.topic) delete body.topic;
      return unwrap<Resultado>(api.POST('/api/v1/admin/ai/cases/generate', { body }));
    },
    onSuccess: (r) => {
      setUltimo(r);
      void qc.invalidateQueries({ queryKey: ['ia'] });
      if (r.jobId)
        toast.success('Lote en cola: se generan en segundo plano (revisa el avance abajo).');
      else toast.success(`${r.caseIds.length} caso(s) generado(s); quedaron en revisión.`);
      if (r.errors.length) toast.warning(`${r.errors.length} intento(s) fallaron: ${r.errors[0]}`);
    },
    onError: (e) =>
      toast.error(e instanceof Error && e.name !== 'ApiError' ? e.message : mensajeDe(e)),
  });
  const cancelar = useMutation({
    mutationFn: (id: number) =>
      ensureOk(
        api.POST('/api/v1/admin/ai/jobs/{id}/cancel', { params: { path: { id: String(id) } } }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['ia', 'trabajos'] }),
    onError: (e) => toast.error(mensajeDe(e)),
  });

  return (
    <>
      <Encabezado
        titulo="Generar casos con IA"
        descripcion="Los casos quedan en revisión; nada llega al banco sin que alguien lo apruebe."
      />
      <div className="grid gap-6 xl:grid-cols-[3fr_2fr]">
        <form
          className="panel-vidrio space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            generar.mutate();
          }}
        >
          <div className="flex gap-2" role="radiogroup" aria-label="Fuente">
            {(
              [
                ['caso_desde_texto', 'Desde un tema o texto'],
                ['caso_desde_imagen', 'Desde una imagen'],
              ] as const
            ).map(([k, t]) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={f.task === k}
                className={`btn-cs ${f.task === k ? 'btn-primario' : 'btn-vidrio'}`}
                onClick={() => poner({ task: k })}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo etiqueta="Especialidad">
              <select
                className="input-vidrio"
                value={f.specialtyId ?? ''}
                onChange={(e) =>
                  poner({ specialtyId: e.target.value ? Number(e.target.value) : undefined })
                }
              >
                <option value="">Sin especificar</option>
                {esp.data?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {nombreEspecialidad(e)}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo etiqueta={desdeImagen ? 'Contexto (opcional)' : 'Tema'}>
              <input
                className="input-vidrio"
                maxLength={500}
                value={f.topic ?? ''}
                placeholder="Ej. preeclampsia con criterios de severidad"
                onChange={(e) => poner({ topic: e.target.value })}
              />
            </Campo>
          </div>
          {desdeImagen ? (
            <Campo
              etiqueta="Imagen (radiografía, ECG, foto clínica, diapositiva…)"
              ayuda="Requiere un modelo con visión. Máx. 5 MB."
            >
              <label className="btn-cs btn-contorno w-fit cursor-pointer">
                <ImagePlus size={16} aria-hidden /> {imagen ? imagen.name : 'Elegir imagen'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="sr-only"
                  onChange={(e) => setImagen(e.target.files?.[0] ?? null)}
                />
              </label>
            </Campo>
          ) : (
            <Campo
              etiqueta="Material de referencia (opcional)"
              ayuda="Pega un fragmento de manual, resumen GPC o diapositivas: el caso se basará en él."
            >
              <textarea
                className="input-vidrio min-h-36"
                maxLength={20000}
                value={f.sourceText ?? ''}
                onChange={(e) => poner({ sourceText: e.target.value })}
              />
            </Campo>
          )}
          <div className="grid gap-3 sm:grid-cols-4">
            <Campo etiqueta="Dificultad">
              <select
                className="input-vidrio"
                value={f.difficulty}
                onChange={(e) => poner({ difficulty: e.target.value as Body['difficulty'] })}
              >
                <option value="basico">Básica</option>
                <option value="intermedio">Intermedia</option>
                <option value="avanzado">Avanzada</option>
              </select>
            </Campo>
            <Campo etiqueta="Idioma">
              <select
                className="input-vidrio"
                value={f.language}
                onChange={(e) => poner({ language: e.target.value as Body['language'] })}
              >
                <option value="es">Español</option>
                <option value="en">Inglés</option>
              </select>
            </Campo>
            <Campo etiqueta="Preguntas por caso">
              <select
                className="input-vidrio"
                value={f.questions}
                onChange={(e) => poner({ questions: Number(e.target.value) })}
              >
                {[1, 2, 3].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo etiqueta="Casos a generar" ayuda="Más de 3: en cola">
              <input
                type="number"
                min={1}
                max={desdeImagen ? 3 : 50}
                className="input-vidrio"
                value={f.count}
                onChange={(e) => poner({ count: Number(e.target.value) })}
              />
            </Campo>
          </div>
          {editable && (
            <button type="submit" className="btn-cs btn-primario" disabled={generar.isPending}>
              <Sparkles size={16} aria-hidden />{' '}
              {generar.isPending ? 'Generando… (puede tardar un minuto)' : 'Generar'}
            </button>
          )}
          {ultimo && ultimo.caseIds.length > 0 && (
            <p className="text-sm">
              Listo:{' '}
              <Link to={`/ia/revision?caso=${ultimo.caseIds[0]}`}>
                revisar{' '}
                {ultimo.caseIds.length === 1 ? 'el caso' : `los ${ultimo.caseIds.length} casos`}
              </Link>
              .
            </p>
          )}
        </form>
        <section className="panel-vidrio space-y-3">
          <h2 className="text-lg font-extrabold">Lotes en cola</h2>
          {hayActivos && (
            <p className="text-xs text-texto-3">El servidor avanza la cola cada minuto.</p>
          )}
          {(trabajos.data ?? []).length === 0 && <p className="text-sm text-texto-3">Sin lotes.</p>}
          <ul className="divide-y divide-[var(--separador)]">
            {trabajos.data?.map((t) => (
              <li key={t.id} className="py-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">
                    #{t.id} · {ESTADO_TRABAJO[t.status]}
                  </span>
                  <span className="tabular">
                    {t.done}/{t.total}
                    {t.failed > 0 && ` · ${t.failed} fallas`}
                  </span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[var(--pista)]">
                  <div
                    className="h-full bg-cian-500"
                    style={{
                      width: `${Math.round(((t.done + t.failed) / Math.max(1, t.total)) * 100)}%`,
                    }}
                  />
                </div>
                <p className="mt-1 text-xs text-texto-3">
                  {fechaHora(t.createdAt)}
                  {t.error && ` · ${t.error}`}
                </p>
                {editable && (t.status === 'queued' || t.status === 'running') && (
                  <button
                    type="button"
                    className="mt-1 text-xs font-semibold"
                    onClick={() => cancelar.mutate(t.id)}
                  >
                    Cancelar
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </>
  );
}

export function GenerarPage() {
  return (
    <PermissionGate seccion="ia">
      <Generar />
    </PermissionGate>
  );
}
